import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPaperPlane, faXmark } from '@fortawesome/free-solid-svg-icons';
import RichTextEditor from '../components/shared/RichTextEditor';
import TicketAttachmentUploader, { AttachmentItem } from '../components/shared/TicketAttachmentUploader';

type TicketStatus = 'open' | 'in_progress' | 'waiting_merchant' | 'resolved' | 'closed';

interface Ticket {
  id: string;
  ticket_number: string;
  subject: string;
  description_html: string;
  category: string;
  priority: string;
  status: TicketStatus;
  created_at: string;
}
interface Message {
  id: string;
  sender_type: 'merchant' | 'employee' | 'admin' | 'admin_employee' | 'system';
  sender_name: string | null;
  body_html: string;
  created_at: string;
}
interface RelatedOrder {
  id: number;
  ref_type: 'order' | 'payout' | 'settlement';
  ref_id: string;
  ref_number: string;
  ref_amount: number | null;
  ref_status: string | null;
}
interface Attachment {
  id: string;
  ticket_id: string;
  message_id: string | null;
  file_url: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
}

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: 'เปิด', in_progress: 'กำลังดำเนินการ', waiting_merchant: 'รอ Merchant ตอบ',
  resolved: 'แก้ไขแล้ว', closed: 'ปิด',
};
const STATUS_COLOR: Record<TicketStatus, string> = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  waiting_merchant: 'bg-purple-100 text-purple-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-100 text-slate-600',
};
const TYPE_COLOR: Record<string, string> = {
  order: 'bg-blue-50 text-blue-700 border-blue-200',
  payout: 'bg-purple-50 text-purple-700 border-purple-200',
  settlement: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

function fmtDateTime(s?: string | null) {
  if (!s) return '-';
  return new Date(s).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' });
}
function fmtBytes(n?: number) {
  if (!n) return '';
  const u = ['B', 'KB', 'MB', 'GB']; let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 ? 0 : 1)}${u[i]}`;
}

const SafeHtml: React.FC<{ html: string }> = ({ html }) => {
  const clean = useMemo(() => DOMPurify.sanitize(html || '', { USE_PROFILES: { html: true } }), [html]);
  return <div className="prose prose-sm max-w-none break-words" dangerouslySetInnerHTML={{ __html: clean }} />;
};

const TicketDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [related, setRelated] = useState<RelatedOrder[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const [replyHtml, setReplyHtml] = useState('');
  const [replyAttachments, setReplyAttachments] = useState<AttachmentItem[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    if (!id) return;
    setLoading(true);
    merchantApi.get(`/tickets/${id}`)
      .then(res => {
        const d = res.data?.data;
        setTicket(d?.ticket || null);
        setMessages(d?.messages || []);
        setRelated(d?.related_orders || []);
        setAttachments(d?.attachments || []);
      })
      .catch(() => toast.error('โหลด ticket ไม่สำเร็จ'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const ticketAttachments = useMemo(() => attachments.filter(a => !a.message_id), [attachments]);
  const messageAttachments = (mid: string) => attachments.filter(a => a.message_id === mid);

  async function submitReply() {
    const text = replyHtml.replace(/<[^>]+>/g, '').trim();
    if (!text) {
      toast.warn('กรุณากรอกข้อความ');
      return;
    }
    setSubmitting(true);
    try {
      const res = await merchantApi.post(`/tickets/${id}/reply`, {
        body_html: replyHtml,
        attachments: replyAttachments.map(a => ({ file_url: a.file_url, file_name: a.file_name, mime_type: a.mime_type, size_bytes: a.size_bytes })),
      });
      if (res.data?.success) {
        setReplyHtml('');
        setReplyAttachments([]);
        toast.success('ส่งคำตอบแล้ว');
        load();
      } else {
        toast.error(res.data?.message || 'เกิดข้อผิดพลาด');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'ส่งคำตอบไม่สำเร็จ');
    } finally {
      setSubmitting(false);
    }
  }

  async function closeTicket() {
    if (!window.confirm('ปิด ticket นี้?')) return;
    try {
      await merchantApi.post(`/tickets/${id}/close`);
      toast.success('ปิด ticket แล้ว');
      load();
    } catch {
      toast.error('ปิดไม่สำเร็จ');
    }
  }

  if (loading) {
    return <div className="p-8 text-center text-slate-400">กำลังโหลด...</div>;
  }
  if (!ticket) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500">ไม่พบ ticket</p>
        <Link to="/tickets" className="text-blue-600 hover:underline text-sm">กลับไปที่รายการ</Link>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto">
      <div className="mb-4">
        <button onClick={() => navigate('/tickets')} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800">
          <FontAwesomeIcon icon={faArrowLeft} /> กลับ
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        {/* Header */}
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-xs text-slate-500">{ticket.ticket_number}</span>
                <span className={`px-2 py-0.5 text-[11px] rounded-full ${STATUS_COLOR[ticket.status]}`}>
                  {STATUS_LABEL[ticket.status]}
                </span>
                <span className="text-[11px] uppercase tracking-wider text-slate-400">{ticket.category}</span>
                <span className="text-[11px] uppercase tracking-wider text-slate-400">priority: {ticket.priority}</span>
              </div>
              <h1 className="text-xl font-bold text-slate-900">{ticket.subject}</h1>
              <p className="text-xs text-slate-400 mt-1">เปิดเมื่อ {fmtDateTime(ticket.created_at)}</p>
            </div>
            {ticket.status !== 'closed' && (
              <button
                onClick={closeTicket}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs text-slate-600 border border-slate-200 hover:bg-slate-50 rounded-lg"
              >
                <FontAwesomeIcon icon={faXmark} /> ปิด Ticket
              </button>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="p-5 border-b border-slate-100">
          <SafeHtml html={ticket.description_html} />
          {ticketAttachments.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {ticketAttachments.map(a => (
                <a
                  key={a.id}
                  href={a.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-xs text-slate-700"
                >
                  📎 <span className="truncate max-w-[200px]">{a.file_name}</span>
                  <span className="text-slate-400">{fmtBytes(a.size_bytes)}</span>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Related orders */}
        {related.length > 0 && (
          <div className="p-5 border-b border-slate-100">
            <div className="text-xs font-semibold text-slate-500 uppercase mb-2">Order ที่เกี่ยวข้อง</div>
            <div className="flex flex-wrap gap-1.5">
              {related.map(r => (
                <span key={r.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border ${TYPE_COLOR[r.ref_type] || ''}`}>
                  <span className="font-semibold uppercase text-[9px]">{r.ref_type}</span>
                  <span className="font-mono">{r.ref_number}</span>
                  {typeof r.ref_amount === 'number' && (
                    <span className="text-slate-600">฿{Number(r.ref_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="p-5 space-y-4">
          {messages.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4">ยังไม่มีการตอบกลับ</p>
          ) : messages.map(m => {
            const isMine = m.sender_type === 'merchant' || m.sender_type === 'employee';
            const isSystem = m.sender_type === 'system';
            const isAdmin = m.sender_type === 'admin' || m.sender_type === 'admin_employee';
            if (isSystem) {
              return (
                <div key={m.id} className="text-center text-xs text-slate-400">
                  <SafeHtml html={m.body_html} />
                  <span>· {fmtDateTime(m.created_at)}</span>
                </div>
              );
            }
            return (
              <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  isMine ? 'bg-blue-600 text-white' : (isAdmin ? 'bg-slate-100 text-slate-800' : 'bg-slate-50 text-slate-800')
                }`}>
                  <div className={`text-[11px] mb-1 ${isMine ? 'text-blue-100' : 'text-slate-500'}`}>
                    {m.sender_name || (isMine ? 'คุณ' : 'ทีมงาน')} · {fmtDateTime(m.created_at)}
                  </div>
                  <div className={`${isMine ? 'prose-invert' : ''}`}>
                    <SafeHtml html={m.body_html} />
                  </div>
                  {messageAttachments(m.id).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {messageAttachments(m.id).map(a => (
                        <a
                          key={a.id}
                          href={a.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] ${
                            isMine ? 'bg-blue-700 hover:bg-blue-800 text-white' : 'bg-white hover:bg-slate-50 border border-slate-200 text-slate-700'
                          }`}
                        >
                          📎 <span className="truncate max-w-[160px]">{a.file_name}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Reply box */}
        {ticket.status !== 'closed' && (
          <div className="p-5 border-t border-slate-100 bg-slate-50/50 rounded-b-xl space-y-3">
            <div className="text-xs font-semibold text-slate-600">ตอบกลับ</div>
            <RichTextEditor value={replyHtml} onChange={setReplyHtml} placeholder="พิมพ์คำตอบ..." minHeight={120} />
            <TicketAttachmentUploader value={replyAttachments} onChange={setReplyAttachments} />
            <div className="flex justify-end">
              <button
                onClick={submitReply}
                disabled={submitting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-sm font-medium"
              >
                <FontAwesomeIcon icon={faPaperPlane} /> {submitting ? 'กำลังส่ง...' : 'ส่ง'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketDetailPage;
